import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaFacebook, FaTwitter, FaInstagram, FaLinkedin, FaEnvelope, FaPhone, FaMapMarkerAlt, FaCode, FaRocket, FaLaptopCode, FaHeart } from 'react-icons/fa';
import OutboundLink from '../common/OutboundLinkTracker';

const Footer = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.2
      } 
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { duration: 0.5, ease: "easeOut" }
    }
  };
  
  const linkVariants = {
    initial: { x: 0 },
    hover: { x: 5, transition: { duration: 0.2 } }
  };
  
  const iconVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.2, rotate: 5, transition: { duration: 0.2 } }
  };

  return (
    <footer className="relative bg-gradient-to-b from-gray-900 to-gray-950 text-white pt-20 pb-10 overflow-hidden">
      {/* Éléments décoratifs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent"></div>
        
        <motion.div 
          className="absolute top-20 right-10 w-64 h-64 rounded-full bg-primary/5 blur-3xl"
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.1, 0.15, 0.1],
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            repeatType: "reverse" 
          }}
        />
        
        <motion.div 
          className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-accent/5 blur-3xl"
          animate={{ 
            scale: [1, 1.3, 1],
            opacity: [0.05, 0.1, 0.05],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            repeatType: "reverse" 
          }}
        />
        
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[length:20px_20px]"></div>
      </div>
      
      <div className="container mx-auto px-6 relative z-10">
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Company Info - Occupe 5 colonnes */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="mb-8 relative">
              <Link to="/" className="inline-block">
                <div className="relative inline-block">
                  <motion.img 
                    src={`${process.env.PUBLIC_URL}/logo.png`} 
                    alt="Nexlyx Logo" 
                    className="relative h-40 z-10 w-auto" 
                    whileHover={{ filter: "drop-shadow(0 0 20px rgba(59, 130, 246, 0.9))" }}
                  />
                  
                  {/* Halo effect - ajusté pour correspondre au logo */}
                  <motion.div 
                    className="absolute -inset-4 rounded-full bg-gradient-to-r from-blue-500/20 to-primary/20 opacity-30 blur-xl -z-10"
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0.2, 0.4, 0.2]
                    }}
                    transition={{ 
                      duration: 4, 
                      repeat: Infinity,
                      repeatType: "reverse" 
                    }}
                  />
                  
                  {/* Pulsating circle - ajusté pour correspondre au logo */}
                  <motion.div 
                    className="absolute bottom-1/4 left-8 translate-x-1 -translate-y-1 w-24 h-24 rounded-full border border-blue-400/30 -z-10"
                    animate={{ 
                      scale: [2, 1.5, 2],
                      opacity: [0.5, 0, 0.5]
                    }}
                    transition={{ 
                      duration: 3, 
                      repeat: Infinity,
                      repeatType: "reverse" 
                    }}
                  />
                  
                  {/* Decorative particles */}
                  <motion.div 
                    className="absolute -top-2 right-0 text-primary text-sm"
                    animate={{ 
                      y: [-5, -12, -5],
                      x: [0, 5, 0],
                      opacity: [0, 0.8, 0],
                      rotate: [0, 15, 0]
                    }}
                    transition={{ 
                      duration: 3, 
                      repeat: Infinity,
                      repeatType: "loop" 
                    }}
                  >
                    <FaCode />
                  </motion.div>
                  
                  <motion.div 
                    className="absolute bottom-0 -left-2 text-accent text-sm"
                    animate={{ 
                      y: [0, -8, 0],
                      x: [-2, -6, -2],
                      opacity: [0, 0.8, 0],
                      rotate: [0, -10, 0]
                    }}
                    transition={{ 
                      duration: 2.5, 
                      repeat: Infinity,
                      repeatType: "loop",
                      delay: 0.5
                    }}
                  >
                    <FaRocket />
                  </motion.div>
                </div>
              </Link>
            </div>
            
            <motion.p 
              className="text-gray-400 mb-4 leading-relaxed text-sm"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
            >
              Nexlyx est une agence de développement web spécialisée dans la création de solutions digitales sur mesure pour les entreprises et les startups.
            </motion.p>
            
            <motion.div 
              className="flex space-x-5 mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <motion.div
                variants={iconVariants}
                initial="initial"
                whileHover={{ y: -3 }}
              >
                <OutboundLink 
                  href="https://www.instagram.com/simon_lp04/" 
                  className="bg-gray-800 hover:bg-gradient-to-br hover:from-[#E1306C] hover:to-[#FCAF45] text-gray-300 hover:text-white p-3 rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{ boxShadow: "0 10px 15px -3px rgba(225, 48, 108, 0)" }}
                  whileHover={{ boxShadow: "0 10px 15px -3px rgba(225, 48, 108, 0.3)" }}
                >
                  <FaInstagram size={18} />
                </OutboundLink>
              </motion.div>
              
              <motion.div
                variants={iconVariants}
                initial="initial"
                whileHover={{ y: -3 }}
              >
                <OutboundLink 
                  href="https://www.linkedin.com/in/simon-leplat-ab933b273/" 
                  className="bg-gray-800 hover:bg-[#0077B5] text-gray-300 hover:text-white p-3 rounded-full transition-all duration-300 flex items-center justify-center"
                  style={{ boxShadow: "0 10px 15px -3px rgba(0, 119, 181, 0)" }}
                  whileHover={{ boxShadow: "0 10px 15px -3px rgba(0, 119, 181, 0.3)" }}
                >
                  <FaLinkedin size={18} />
                </OutboundLink>
              </motion.div>
            </motion.div>
            
            <motion.div
              className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-5 border border-gray-700/50"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              viewport={{ once: true }}
              whileHover={{ boxShadow: "0 0 30px rgba(59, 130, 246, 0.1)" }}
            >
              <h4 className="text-white font-semibold mb-3 flex items-center">
                <FaHeart className="text-accent mr-2" /> Notre mission
              </h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                Transformer vos idées en solutions digitales innovantes et performantes, en plaçant l'expérience utilisateur et la qualité technique au cœur de notre démarche.
              </p>
            </motion.div>
          </motion.div>

          {/* Liens Rapides - Occupe 2 colonnes */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <h3 className="text-lg font-bold mb-5 text-white relative inline-block">
              Liens Rapides
              <motion.div 
                className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-primary to-transparent w-full"
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ delay: 0.2, duration: 0.4 }}
                viewport={{ once: true }}
              />
            </h3>
            
            <ul className="space-y-3">
              {[
                { path: '/', name: 'Accueil' },
                { path: '/services', name: 'Services' },
                { path: '/portfolio', name: 'Portfolio' },
                { path: '/about', name: 'À Propos' },
                { path: '/blog', name: 'Blog' },
                { path: '/faq', name: 'FAQ' },
                { path: '/contact', name: 'Contact' },
                { path: '/audit', name: 'Expertise gratuite' },
                { path: '/privacy', name: 'Confidentialité' },
                { path: '/terms', name: 'Conditions' }
              ].map((link, index) => (
                <motion.li key={link.name}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Link 
                    to={link.path} 
                    className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <motion.span 
                      className="w-1.5 h-1.5 rounded-full bg-primary mr-2 opacity-0 group-hover:opacity-100"
                      variants={iconVariants}
                      initial="initial"
                      whileHover="hover"
                    />
                    <motion.span variants={linkVariants} initial="initial" whileHover="hover">
                      {link.name}
                    </motion.span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Services - Occupe 2 colonnes */}
          <motion.div variants={itemVariants} className="lg:col-span-2">
            <h3 className="text-lg font-bold mb-5 text-white relative inline-block">
              Nos Services
              <motion.div 
                className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-primary to-transparent w-full"
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ delay: 0.2, duration: 0.4 }}
                viewport={{ once: true }}
              />
            </h3>
            
            <ul className="space-y-3">
              {[
                { path: '/services#site-vitrine', name: 'Création de site vitrine' },
                { path: '/services#refonte', name: 'Refonte de site existant' },
                { path: '/services#seo', name: 'Optimisation SEO' },
                { path: '/services#maintenance', name: 'Maintenance web' },
                { path: '/services#e-commerce', name: 'E-commerce' },
                { path: '/services#applications', name: 'Applications web & mobiles' }
              ].map((service, index) => (
                <motion.li key={service.name}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1 * index, duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  <Link 
                    to={service.path} 
                    className="text-gray-400 hover:text-white transition-colors duration-300 flex items-center group"
                  >
                    <motion.span 
                      className="w-1.5 h-1.5 rounded-full bg-accent mr-2 opacity-0 group-hover:opacity-100"
                      variants={iconVariants}
                      initial="initial"
                      whileHover="hover"
                    />
                    <motion.span variants={linkVariants} initial="initial" whileHover="hover">
                      {service.name}
                    </motion.span>
                  </Link>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info - Occupe 3 colonnes */}
          <motion.div variants={itemVariants} className="lg:col-span-3">
            <h3 className="text-lg font-bold mb-5 text-white relative inline-block">
              Contact
              <motion.div 
                className="absolute -bottom-1 left-0 h-0.5 bg-gradient-to-r from-primary to-transparent w-full"
                initial={{ width: 0 }}
                whileInView={{ width: '100%' }}
                transition={{ delay: 0.2, duration: 0.4 }}
                viewport={{ once: true }}
              />
            </h3>
            
            <ul className="space-y-5">
              <motion.li 
                className="flex items-start group"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ x: 3 }}
              >
                <div className="bg-gray-800 p-2 rounded-lg mr-3 group-hover:bg-primary/20 transition-colors duration-300">
                  <FaMapMarkerAlt className="text-primary group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <span className="text-gray-400 text-sm leading-relaxed block">Route de Font Gamatte,</span>
                  <span className="text-gray-400 text-sm leading-relaxed block">13590 Meyreuil, France</span>
                </div>
              </motion.li>
              
              <motion.li 
                className="flex items-center group"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ x: 3 }}
              >
                <div className="bg-gray-800 p-2 rounded-lg mr-3 group-hover:bg-primary/20 transition-colors duration-300">
                  <FaPhone className="text-primary group-hover:text-white transition-colors duration-300" />
                </div>
                <a href="tel:+33638988909" className="text-gray-400 hover:text-white transition-colors duration-300">
                  06 38 98 89 09
                </a>
              </motion.li>
              
              <motion.li 
                className="flex items-center group"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ x: 3 }}
              >
                <div className="bg-gray-800 p-2 rounded-lg mr-3 group-hover:bg-primary/20 transition-colors duration-300">
                  <FaEnvelope className="text-primary group-hover:text-white transition-colors duration-300" />
                </div>
                <a href="mailto:contact@nexlyx.fr" className="text-gray-400 hover:text-white transition-colors duration-300">
                  contact@nexlyx.fr
                </a>
              </motion.li>
              
              <motion.li 
                className="flex items-start group"
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.3 }}
                viewport={{ once: true }}
                whileHover={{ x: 3 }}
              >
                <div className="bg-gray-800 p-2 rounded-lg mr-3 group-hover:bg-primary/20 transition-colors duration-300">
                  <FaLaptopCode className="text-primary group-hover:text-white transition-colors duration-300" />
                </div>
                <div>
                  <span className="text-gray-400 text-sm leading-relaxed block">SIRET : 99014127700014</span>
                </div>
              </motion.li>
            </ul>
          </motion.div>
        </motion.div>

        {/* Copyright */}
        <motion.div 
          className="border-t border-gray-800/50 mt-16 pt-8 text-center"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 mb-4 md:mb-0">
              &copy; {new Date().getFullYear()} Nexlyx. Tous droits réservés.
            </p>
            <div className="flex space-x-6">
              <Link to="/privacy" className="text-gray-500 hover:text-gray-300 text-sm transition-colors duration-300">
                Politique de confidentialité
              </Link>
              <Link to="/terms" className="text-gray-500 hover:text-gray-300 text-sm transition-colors duration-300">
                Conditions d'utilisation
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
